/**
 * Tiptap Rich Text Editor for Comments
 * Bold, lists, and @mentions of account teammates
 */

import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import Underline from '@tiptap/extension-underline';
import Mention from '@tiptap/extension-mention';
import { Markdown } from '@tiptap/markdown';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';

function loadMentionUsers(element) {
  const scriptId = element.getAttribute('data-mention-users-id');
  if (!scriptId) {
    return [];
  }
  const script = document.getElementById(scriptId);
  if (!script) {
    return [];
  }
  try {
    const parsed = JSON.parse(script.textContent);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Tiptap: Failed to parse mention users', error);
    return [];
  }
}

function filterMentionUsers(users, query) {
  const needle = (query || '').toLowerCase().trim();
  if (!needle) {
    return users.slice(0, 8);
  }
  return users
    .filter((user) => (user.name || '').toLowerCase().includes(needle))
    .slice(0, 8);
}

function buildMentionListElement(props, selectedIndex) {
  const popup = document.createElement('div');
  popup.className = 'mention-suggestions';

  const items = props.items || [];
  if (!items.length) {
    const empty = document.createElement('div');
    empty.className = 'mention-suggestion-empty';
    empty.textContent = 'No teammates match';
    popup.appendChild(empty);
    return popup;
  }

  items.forEach((item, index) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `mention-suggestion${index === selectedIndex ? ' is-selected' : ''}`;

    if (item.avatar_url) {
      const img = document.createElement('img');
      img.src = item.avatar_url;
      img.alt = item.name || '';
      img.className = 'mention-suggestion-avatar';
      button.appendChild(img);
    } else {
      const avatar = document.createElement('span');
      avatar.className = 'mention-suggestion-avatar';
      const initials = (item.name || '?')
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0])
        .join('')
        .toUpperCase();
      avatar.textContent = initials || '?';
      button.appendChild(avatar);
    }

    const label = document.createElement('span');
    label.textContent = item.name || '';
    button.appendChild(label);

    button.addEventListener('mousedown', (event) => {
      event.preventDefault();
      props.command({ id: item.id, label: item.name });
    });
    popup.appendChild(button);
  });

  return popup;
}

/**
 * TipTap's standard suggestion render path: Tippy owns positioning/scroll.
 * See https://tiptap.dev/docs/editor/extensions/nodes/mention
 */
function createSuggestionRenderer() {
  let popup;
  let selectedIndex = 0;
  let currentProps = null;

  const refreshContent = () => {
    if (!popup || !currentProps) {
      return;
    }
    popup.setContent(buildMentionListElement(currentProps, selectedIndex));
  };

  return {
    onStart(props) {
      selectedIndex = 0;
      currentProps = props;

      if (!props.clientRect) {
        return;
      }

      popup = tippy('body', {
        getReferenceClientRect: props.clientRect,
        appendTo: () => document.body,
        content: buildMentionListElement(props, selectedIndex),
        showOnCreate: true,
        interactive: true,
        trigger: 'manual',
        placement: 'bottom-start',
        theme: 'mention',
        offset: [0, 4],
      })[0];
    },

    onUpdate(props) {
      selectedIndex = 0;
      currentProps = props;

      if (!popup) {
        return;
      }

      popup.setProps({
        getReferenceClientRect: props.clientRect,
      });
      refreshContent();
    },

    onKeyDown({ event }) {
      if (!popup || !currentProps) {
        return false;
      }

      const items = currentProps.items || [];

      if (event.key === 'Escape') {
        popup.hide();
        return true;
      }
      if (event.key === 'ArrowDown') {
        selectedIndex = items.length ? (selectedIndex + 1) % items.length : 0;
        refreshContent();
        return true;
      }
      if (event.key === 'ArrowUp') {
        selectedIndex = items.length ? (selectedIndex - 1 + items.length) % items.length : 0;
        refreshContent();
        return true;
      }
      if (event.key === 'Enter') {
        const item = items[selectedIndex];
        if (item) {
          currentProps.command({ id: item.id, label: item.name });
        }
        return true;
      }
      return false;
    },

    onExit() {
      if (popup) {
        popup.destroy();
        popup = null;
      }
      currentProps = null;
    },
  };
}

function markdownFromEditor(editor) {
  let markdown = editor.getMarkdown() || '';
  markdown = markdown.replace(
    /\[@\s*((?:(?:id|label|char)="[^"]*"\s*)*)\]/g,
    (full, attrs) => {
      const idMatch = /id="([^"]*)"/.exec(attrs);
      const labelMatch = /label="([^"]*)"/.exec(attrs);
      if (!idMatch) {
        return full;
      }
      const label = labelMatch ? labelMatch[1] : '';
      return `[@${label}](mention:${idMatch[1]})`;
    },
  );
  return markdown;
}

/**
 * Initialize a Tiptap editor instance
 * @param {string} elementId - The ID of the element to mount the editor to
 * @param {string} hiddenInputId - The ID of the hidden input to store HTML content
 * @returns {Editor} The Tiptap editor instance
 */
export function initTiptapEditor(elementId, hiddenInputId) {
  const element = document.getElementById(elementId);
  const hiddenInput = document.getElementById(hiddenInputId);
  
  if (!element || !hiddenInput) {
    console.error('Tiptap: Element or hidden input not found', { elementId, hiddenInputId });
    return null;
  }

  const placeholder = element.getAttribute('data-placeholder') || 'Start typing...';
  const mentionUsers = loadMentionUsers(element);
  const initialMarkdown = (hiddenInput.value || '').trim();

  const editor = new Editor({
    element: element,
    extensions: [
      StarterKit.configure({
        // Disable list extensions from StarterKit since we're adding them separately
        bulletList: false,
        orderedList: false,
        listItem: false,
        // Keep what we need
        bold: true,
        italic: true,
        paragraph: true,
        text: true,
        history: true,
        hardBreak: true,
        dropcursor: true,
        gapcursor: true,
        // Disable features we don't need
        heading: false,
        horizontalRule: false,
        code: false,
        codeBlock: false,
        blockquote: false,
        strike: false,
      }),
      // Add list and formatting extensions separately for Tiptap v3
      BulletList,
      OrderedList,
      ListItem,
      Underline,
      Mention.configure({
        HTMLAttributes: {
          class: 'mention-chip',
        },
        renderText({ node }) {
          return `[@${node.attrs.label || ''}](mention:${node.attrs.id})`;
        },
        suggestion: {
          char: '@',
          allowSpaces: true,
          items: ({ query }) => filterMentionUsers(mentionUsers, query),
          render: () => createSuggestionRenderer(),
        },
      }),
      // Add Markdown support
      Markdown,
    ],
    content: initialMarkdown || '',
    contentType: initialMarkdown ? 'markdown' : undefined,
    editorProps: {
      attributes: {
        class: 'tiptap-content prose prose-sm max-w-none focus:outline-none min-h-[80px] p-3',
        'data-placeholder': placeholder,
      },
    },
    // Update hidden input with Markdown on content change
    onUpdate: ({ editor }) => {
      hiddenInput.value = markdownFromEditor(editor);
    },
  });

  // Keep hidden input in sync when editing prefilled content
  if (initialMarkdown) {
    hiddenInput.value = markdownFromEditor(editor);
  }

  // Store editor instance on the element for later access
  element.tiptapEditor = editor;

  const form = element.closest('form');
  if (form) {
    form.addEventListener('submit', () => {
      hiddenInput.value = markdownFromEditor(editor);
    });
  }
  
  // Setup toolbar buttons
  setupToolbarButtons(elementId, editor);

  return editor;
}

/**
 * Setup toolbar button click handlers
 * @param {string} editorId - The ID of the editor element
 * @param {Editor} editor - The Tiptap editor instance
 */
function setupToolbarButtons(editorId, editor) {
  const toolbarButtons = document.querySelectorAll(`[data-editor="${editorId}"]`);
  
  toolbarButtons.forEach(button => {
    const action = button.getAttribute('data-action');
    
    button.addEventListener('click', (e) => {
      e.preventDefault();
      
      switch(action) {
        case 'bold':
          editor.chain().focus().toggleBold().run();
          break;
        case 'italic':
          editor.chain().focus().toggleItalic().run();
          break;
        case 'underline':
          editor.chain().focus().toggleUnderline().run();
          break;
        case 'bulletList':
          editor.chain().focus().toggleBulletList().run();
          break;
      }
      
      // Update button active state
      updateToolbarButtonStates(editorId, editor);
    });
  });
  
  // Update button states on selection change
  editor.on('selectionUpdate', () => {
    updateToolbarButtonStates(editorId, editor);
  });
  
  // Initial button state update
  updateToolbarButtonStates(editorId, editor);
}

/**
 * Update toolbar button active states based on current editor state
 * @param {string} editorId - The ID of the editor element
 * @param {Editor} editor - The Tiptap editor instance
 */
function updateToolbarButtonStates(editorId, editor) {
  const toolbarButtons = document.querySelectorAll(`[data-editor="${editorId}"]`);
  
  toolbarButtons.forEach(button => {
    const action = button.getAttribute('data-action');
    let isActive = false;
    
    switch(action) {
      case 'bold':
        isActive = editor.isActive('bold');
        break;
      case 'italic':
        isActive = editor.isActive('italic');
        break;
      case 'underline':
        isActive = editor.isActive('underline');
        break;
      case 'bulletList':
        isActive = editor.isActive('bulletList');
        break;
    }
    
    if (isActive) {
      button.classList.add('is-active');
    } else {
      button.classList.remove('is-active');
    }
  });
}

/**
 * Initialize Tiptap editors for all comment forms on the page
 * Called automatically via HTMX after content swap
 */
export function initAllCommentEditors() {
  const editorContainers = document.querySelectorAll('[data-tiptap-editor]');
  
  editorContainers.forEach(container => {
    const editorId = container.id;
    const hiddenInputId = container.getAttribute('data-hidden-input');
    
    // Don't reinitialize if already exists
    if (container.tiptapEditor) {
      return;
    }
    
    initTiptapEditor(editorId, hiddenInputId);
  });
}

// Auto-initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  initAllCommentEditors();
});

// Re-initialize after HTMX content swaps
document.body.addEventListener('htmx:afterSwap', () => {
  initAllCommentEditors();
});
